// src/context/parseApiError.js
//
// Parses the uniform API error envelope into an Error that carries the
// structured fields (error_code, severity, suggested_action, details, plus any
// top-level extras). Callers map `err.code` → UX; never string-match
// `err.message`.
//
// Envelope shape (see <BACKEND_FOLDER>/docs/frontend/api-contract.md) — it is **FLAT**:
//   { error_code, message, severity, is_retryable, suggested_action,
//     details?, request_id?, ...extras }
//
// ⚠️ "Flat" is load-bearing. `q/src/api/errors.py:128` spreads `**exc.detail`
// into the response body, so extras — CUSTOMER_NOT_INVOICEABLE's `missing[]`,
// for instance — arrive at the TOP level, NOT under `details` and NOT under
// `detail`. Anything reading them out of `details` will silently find nothing.
//
// ⚠️ `suggested_action` is an open hint field, **not an enum**. Branch on
// `error_code`; read `suggested_action` only to colour copy you already chose.

import i18n from '../i18n/index.js';

// FE copy for known error codes lives in errors.json (errors:context.<CODE>).
// Codes listed here resolve to localized FE strings; anything else falls back to
// the server-provided body.message (passthrough — never localized).
const KNOWN_CODES = [
  // Documents (Block 3)
  'DOCUMENT_INVALID_FILE_TYPE',
  'DOCUMENT_NOT_FOUND',
  'RESOURCE_NOT_FOUND',
  // Entities (Blocks 1–2, wired here for reuse)
  'CONTEXT_ENTITY_NOT_FOUND',
  'CONTEXT_ENTITY_INVALID',
  'CONTEXT_ENTITY_CONFLICT',
  'CONTEXT_ETAG_MISMATCH',
  'INVALID_ENTITY_ID',
  // Personas — the server message is English and this is an Italian-market
  // product, so the 409 gets FE copy like the other known codes. There is no
  // `force` and no `details` behind it: the message IS the whole affordance.
  'CANNOT_DISABLE_LAST_PERSONA',
  // Site Manager (KB slices 3–4, 2026-08-28/29) — the server messages are
  // English, and these are states the user must read in their own language.
  'KB_JOB_ACTIVE',
  'KB_JOB_CAPACITY',
  'KB_JOB_LOOKUP_FAILED',
  'KB_SHADOW_EMPTY',
  'KB_SHADOW_PROMOTE_FAILED',
  'KB_SHADOW_DISCARD_FAILED',
  'CONTEXT_URL_SURGERY_FAILED',
  'CONTEXT_IDENTITY_SURGERY_FAILED',
  'CONTEXT_INVALID_SITE_URL',
  // ⛔ NOT here, on purpose: CONTEXT_EXCLUDE_FILE_INVALID and
  // CONTEXT_URL_DELTA_REFUSED (the contract says render `message` verbatim —
  // it names the broken file / the offending URL; the screen prefixes its own
  // headline), SITE_APEX_CHANGED (a dialog built from `details`, never a
  // sentence), and the 402 family + TENANT_RETIRED (worded by
  // utils/runRefusal.js through RunRefusalNotice).
];

// 403s that already say what they mean, and must therefore survive the generic
// "admin only" rewrite below.
//
// ⚠️ Why this set exists: the rewrite was written when the only 403 reaching
// here was a context-API write denial, and it clobbered EVERY other 403 into
// "Operazione riservata agli amministratori". PB4/D10 added two 403s that are
// nothing of the sort — a retired workspace and an application the workspace
// was never assigned — and turning either into "admin only" would hide the real
// reason and point the user at the wrong remedy. Grow this set whenever a 403
// code gains copy of its own.
const SELF_DESCRIBED_403_CODES = new Set([
  'TENANT_ACCESS_DENIED',  // intercepted inside apiFetch; never reaches here
  'TENANT_RETIRED',        // P8 half A — workspace retired, not a permission fact
  // The application is not assigned to this workspace — an OPERATOR act, not
  // a permission denial. Replaced FORMAT_NOT_IN_PLAN, withdrawn 2026-09-20.
  'APP_NOT_ASSIGNED',
]);

// Envelope keys we read explicitly. Everything else in the body is an "extra"
// and is carried through verbatim (see `err.extras` below).
const ENVELOPE_KEYS = new Set([
  'error_code', 'message', 'severity', 'is_retryable', 'suggested_action',
  'details', 'request_id',
  'detail', // legacy/nested envelope; never an extra
]);

// Names an extra may not overwrite when it is hoisted onto the Error.
const RESERVED_ERROR_KEYS = new Set([
  'name', 'message', 'stack', 'cause',
  'status', 'code', 'severity', 'suggestedAction', 'isRetryable', 'details',
  'requestId', 'extras',
]);

/**
 * Build a rich Error from an already-parsed envelope body.
 *
 * Split out of `parseApiError` so that callers outside the context API — the
 * pipeline run, whose refusals must be branched on by code — get the same
 * structured Error without inheriting the context-domain copy rewrites below.
 * One parser, two entry points; never a second implementation.
 *
 * @param {object} body parsed JSON body ({} when the body was empty/non-JSON)
 * @param {number} status HTTP status
 * @param {{fallbackMessage?: string, withRequestId?: boolean}} [opts]
 *   `fallbackMessage` replaces the generic "Errore (status)" when the server
 *   sent no message; `withRequestId` appends the " (ref: …)" suffix that the
 *   pipeline caller has always shown.
 * @returns {Error} carrying code/status/details/suggestedAction/isRetryable/extras
 */
export function buildApiError(body, status, { fallbackMessage, withRequestId = false } = {}) {
  const envelope = (body && typeof body === 'object') ? body : {};
  const code = envelope.error_code || '';

  let message = envelope.message
    || fallbackMessage
    || i18n.t('errors:context.generic', { status });
  if (withRequestId && envelope.request_id) {
    message += ` (ref: ${envelope.request_id})`;
  }

  const err = new Error(message);
  err.status = status;
  err.code = code;
  err.severity = envelope.severity;
  err.suggestedAction = envelope.suggested_action;
  // ⚠️ An open hint, not an enum — never branch on it.
  err.isRetryable = envelope.is_retryable === true;
  err.details = envelope.details; // CONTEXT_ENTITY_INVALID → details.errors[]
  err.requestId = envelope.request_id;

  // Top-level extras, carried whole and also hoisted for convenience
  // (`err.missing`), because the envelope itself is flat.
  const extras = {};
  for (const [key, value] of Object.entries(envelope)) {
    if (ENVELOPE_KEYS.has(key)) continue;
    extras[key] = value;
    if (!RESERVED_ERROR_KEYS.has(key)) err[key] = value;
  }
  err.extras = extras;

  return err;
}

/**
 * Build a rich Error from a non-OK Response, with the context API's localized
 * copy applied. Always `await`-ed by API callers which then `throw` it. Reads
 * the body via res.clone() so the caller can still inspect the original
 * response if needed.
 */
export async function parseApiError(res) {
  let body = {};
  try {
    body = await res.clone().json();
  } catch {
    /* non-JSON / empty body */
  }

  const err = buildApiError(body, res.status);

  // Known codes → localized FE copy. Otherwise the server message passes
  // through verbatim (already set by buildApiError).
  if (KNOWN_CODES.includes(err.code)) {
    err.message = i18n.t(`errors:context.${err.code}`);
  }

  // A 403 with no self-describing code is a write-permission denial. (A
  // TENANT_ACCESS_DENIED 403 is intercepted earlier inside apiFetch and never
  // reaches here.) See SELF_DESCRIBED_403_CODES for why the guard is a set and
  // not a single code comparison.
  if (res.status === 403 && !SELF_DESCRIBED_403_CODES.has(err.code)) {
    err.message = i18n.t('errors:context.adminOnly');
  }

  return err;
}
